import { ChevronLeft } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';

import { ShiftActionOverlay } from '@/components/revision/ShiftActionOverlay';

export interface CorreccionesSplitPanelOverlayProps {
  onAprobar?: () => void | Promise<void>;
  onRechazar?: () => void | Promise<void>;
  disabled?: boolean;
}

interface CorreccionesSplitPanelProps<T> {
  /** Elementos de la lista izquierda (evidencias/audios/etc.). */
  items: T[];
  /** Índice seleccionado (controlado por el padre). */
  selectedIndex: number;
  /** Cambia el elemento seleccionado. */
  onSelect: (index: number) => void;
  getKey: (item: T, index: number) => React.Key;
  /** Contenido de cada fila de la lista izquierda (dot + nombre + estado…). */
  renderItem: (item: T, index: number, active: boolean) => ReactNode;
  /** Contenido del panel derecho para el elemento seleccionado. */
  renderPanel: (item: T) => ReactNode;
  /** Mensaje cuando no hay elemento seleccionado. */
  emptyLabel?: string;
  /** Al cambiar (p.ej. cambio de sub-tab), colapsa el panel en mobile para volver a la lista. */
  resetKey?: unknown;
  /** Props opcionales para desplegar el overlay de acción rápida Shift en los elementos de la lista izquierda. */
  getItemOverlayProps?: (item: T, index: number) => CorreccionesSplitPanelOverlayProps | undefined;
}

/**
 * Layout tipo WhatsApp (lista a la izquierda + conversación/detalle a la derecha) compartido por
 * la vista de correcciones del asesor y del administrador. Es puramente presentacional: el estado
 * (qué elemento está activo, sus acciones) lo maneja el padre vía props. En mobile, seleccionar un
 * elemento muestra el panel con un botón "Volver".
 */
export function CorreccionesSplitPanel<T>({
  items,
  selectedIndex,
  onSelect,
  getKey,
  renderItem,
  renderPanel,
  emptyLabel = 'Selecciona un elemento para ver su conversación.',
  resetKey,
  getItemOverlayProps,
}: CorreccionesSplitPanelProps<T>) {
  const [mobilePanelOpen, setMobilePanelOpen] = useState(false);

  // Al cambiar de contexto (sub-tab), volver a la lista en mobile.
  useEffect(() => {
    setMobilePanelOpen(false);
  }, [resetKey]);

  const selected = items[selectedIndex];

  return (
    <div
      className="admin-card cc-split border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm flex flex-col md:flex-row min-h-112.5"
      data-testid="cc-split"
    >
      <aside
        className={`cc-split__list w-full md:w-72 shrink-0 border-b md:border-b-0 md:border-r border-gray-100 overflow-y-auto ${
          mobilePanelOpen ? 'hidden md:block' : 'block'
        }`}
        data-testid="cc-split-list"
      >
        {items.map((item, i) => {
          const overlay = getItemOverlayProps?.(item, i);
          const btn = (
            <button
              key={getKey(item, i)}
              type="button"
              data-testid="cc-list-item"
              data-active={i === selectedIndex}
              onClick={() => {
                onSelect(i);
                setMobilePanelOpen(true);
              }}
              className={`w-full flex items-center gap-2 px-3 py-2.5 text-left border-b border-gray-50 transition-colors ${
                i === selectedIndex ? 'bg-gray-100' : 'hover:bg-gray-50'
              }`}
            >
              {renderItem(item, i, i === selectedIndex)}
            </button>
          );

          if (overlay && (overlay.onAprobar || overlay.onRechazar)) {
            return (
              <ShiftActionOverlay
                key={getKey(item, i)}
                onAprobar={overlay.onAprobar}
                onRechazar={overlay.onRechazar}
                disabled={overlay.disabled}
              >
                {btn}
              </ShiftActionOverlay>
            );
          }

          return btn;
        })}
      </aside>

      <section
        className={`cc-split__panel flex-1 p-6 flex flex-col gap-4 overflow-y-auto ${mobilePanelOpen ? 'block' : 'hidden md:flex'}`}
        data-testid="cc-split-panel"
      >
        <button
          type="button"
          onClick={() => setMobilePanelOpen(false)}
          className="md:hidden flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-700 self-start"
        >
          <ChevronLeft className="w-4 h-4" /> Volver
        </button>

        {selected ? renderPanel(selected) : <p className="text-sm text-gray-500 text-center py-8">{emptyLabel}</p>}
      </section>
    </div>
  );
}
